'use client';

import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { PhoneNumber } from '@provider-directory/components/phone-number';

import { haversineDistance } from '@provider-directory/functions/distance';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import { getLocationDisplayInfo } from '@provider-directory/functions/location';
import type {
  ProviderLocationResource,
  ProviderResource,
} from '@provider-directory/types/providers.contracts';

export interface ProviderCardLocationProps {
  location: ProviderLocationResource;
  provider: ProviderResource;
  className?: string;
}

export const ProviderCardLocation = ({
  location,
  provider,
  className,
}: ProviderCardLocationProps): ReactElement => {
  const orgContext = useOrgContext();
  const phone = getAvailablePhoneNumber(
    location,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );
  const { displayName, addressLabel } = getLocationDisplayInfo(location, {
    newlineForStreet2: true,
  });
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const centerPointCoords =
    searchParams.get('center-point')?.split(',').map(parseFloat) ?? [];

  const distance =
    location?.coordinates && centerPointCoords.length === 2
      ? haversineDistance(
          {
            lat: location.coordinates.lat,
            lng: location.coordinates.lon,
          },
          {
            lat: centerPointCoords[0],
            lng: centerPointCoords[1],
          },
        )
      : null;

  return (
    <div className={clsx('text-[16px] text-display-2xsmall', className)}>
      {distance !== null && (
        <div
          data-testid={'location-distance'}
          className={
            'flex items-center pb-1 text-display-3xsmall leading-[140%] desktop:text-display-2xsmall'
          }
        >
          <MaterialSymbol
            name={'location_on_fill'}
            size={16}
            className={'mr-1'}
          />
          {distance.toFixed(1)} mi {t('search.distanceAway')}
        </div>
      )}
      <div className={'mb-1 text-body-medium-semibold'}>{displayName}</div>
      <div>
        {addressLabel.split('\n').map((line, idx) => (
          <p key={idx} className={'text-body-medium'}>
            {line}
          </p>
        ))}
      </div>
      {phone ? (
        <div data-testid={'location-phone'} className={'mt-1 text-body-medium'}>
          <PhoneNumber phone={phone} format={'stripped'} />
        </div>
      ) : null}
    </div>
  );
};
